import { useCallback, useState } from 'react'
import type { SelectFromFilterRequest } from '../types/case-container'
import { getFilteredCount } from '../services/selections'
import { useSelectionFetch } from './use-selection/use-selection-fetch'
import { usePendingSync } from './use-selection/use-pending-sync'
import {
  useSelectionMutations,
  type SelectionMutations,
} from './use-selection/use-selection-mutations'
import { useSelectionBulk, type SelectionBulk } from './use-selection/use-selection-bulk'

interface UseSelectionResult extends SelectionMutations, SelectionBulk {
  selectedIds: Set<string>
  count: number
  loading: boolean
  syncing: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useSelection(caseId: string | null): UseSelectionResult {
  const fetchState = useSelectionFetch(caseId)
  const pendingSync = usePendingSync({ caseId, setError: fetchState.setError })
  const mutations = useSelectionMutations(fetchState, pendingSync)
  const bulk = useSelectionBulk(caseId, fetchState, pendingSync)

  return {
    selectedIds: fetchState.selectedIds,
    count: fetchState.selectedIds.size,
    loading: fetchState.loading,
    syncing: pendingSync.syncing,
    error: fetchState.error,
    refetch: fetchState.refetch,
    ...mutations,
    ...bulk,
  }
}

interface UseFilteredCountResult {
  count: number | null
  loading: boolean
  error: string | null
  fetch: (filter: SelectFromFilterRequest) => Promise<number>
}

export function useFilteredCount(caseId: string | null): UseFilteredCountResult {
  const [count, setCount] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(
    async (filter: SelectFromFilterRequest): Promise<number> => {
      if (!caseId) {
        setCount(null)
        return 0
      }
      setLoading(true)
      setError(null)
      try {
        const result = await getFilteredCount(caseId, filter)
        setCount(result.count)
        return result.count
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to get filtered count')
        setCount(null)
        return 0
      } finally {
        setLoading(false)
      }
    },
    [caseId]
  )

  return { count, loading, error, fetch }
}
